import { GetJobQuery, JobStatus } from "generated-graphql/graphql";
import { useEffect, useRef } from "react";

import { useCheckForFile } from "./useCheckForFile";
import { terminalJobStatuses, useLongPollingForJob } from "./useLongPollingForJob";

interface UseLongPollingForJobWithFileOutputOptions {
  timeoutInSeconds?: number;
  onUrlsReady: (urls: string[]) => void;
  onError?: (error: Error) => void;
}

interface UseLongPollingForJobWithFileOutputResult {
  polling: boolean;
  job: GetJobQuery["job"] | undefined;
  pollingTimedOut: boolean;
  startPolling: (jobId: string) => void;
}

/**
 * This hook is used to poll for a job and fetch the file URLs when the job is successful.
 * It is a wrapper around the useLongPollingForJob hook and the useCheckForFile hook.
 */
export function useLongPollingForJobWithFileOutput({
  timeoutInSeconds,
  onUrlsReady,
  onError,
}: UseLongPollingForJobWithFileOutputOptions): UseLongPollingForJobWithFileOutputResult {
  const { polling, result, startPolling } = useLongPollingForJob({
    timeoutInSeconds,
  });
  const { fetchFile } = useCheckForFile();
  const hasHandledResult = useRef(false);

  // Reset state when polling starts
  useEffect(() => {
    if (polling) {
      hasHandledResult.current = false;
    }
  }, [polling]);

  // When the job terminates, fetch file URLs and call onUrlsReady — or call
  // onError when there is nothing to download. A polling timeout is neither:
  // the job may still be running server-side, and consumers handle that
  // through pollingTimedOut.
  useEffect(() => {
    const handleTerminalJob = async (job: NonNullable<GetJobQuery["job"]>) => {
      if (job.status !== JobStatus.Succeeded) {
        if (terminalJobStatuses.includes(job.status)) {
          hasHandledResult.current = true;
          onError?.(new Error("The export job failed."));
        }
        return;
      }

      const output = job.output;
      let keys: string[] = [];
      if (output) {
        if (Array.isArray(output.downloadKeys) && output.downloadKeys.length > 0) {
          keys = output.downloadKeys;
        } else if (output.s3Key) {
          keys = [output.s3Key];
        }
      }
      // A "successful" export that produced no files still means the user gets
      // nothing they asked for — surface it instead of a silent success.
      if (keys.length === 0) {
        hasHandledResult.current = true;
        onError?.(new Error("The export finished, but no files were available to download."));
        return;
      }

      try {
        const urls: string[] = [];
        for (const key of keys) {
          const url = await fetchFile(key);
          if (url) {
            urls.push(url);
          } else {
            throw new Error(`Failed to fetch file for key: ${key}`);
          }
        }
        onUrlsReady(urls);
        hasHandledResult.current = true;
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error("Unknown error fetching files"));
      }
    };

    if (result.job && !result.pollingTimedOut && !hasHandledResult.current) {
      handleTerminalJob(result.job);
    }
  }, [result, fetchFile, onUrlsReady, onError]);

  return {
    polling,
    job: result.job,
    pollingTimedOut: result.pollingTimedOut,
    startPolling,
  };
}
